import { SimpleGrid, Tabs } from '@mantine/core';
import { PageTitle } from 'components/PageTitle';
import { Panel } from 'components/Panel';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { isNonNull } from 'remeda';
import { getQueryParams, setQueryParams } from 'shared/utils/url';

import { isEnabledIntegration } from './components/Integration.utils';
import {
  INTEGRATION_ROUTE,
  Integration,
  type IntegrationCard,
  IntegrationCategory,
  IntegrationQueryParams,
} from './Integrations.constants';
import { getIntegrationsCategories, useGetIntegrationCards } from './Integrations.utils';
import { IntegrationsCard } from './IntegrationsCard';

type IntegrationsProps = {
  isOnBoarding?: boolean;
  onIntegrationSelect?: (integrationName: string) => void;
};

export const Integrations = ({ isOnBoarding = false, onIntegrationSelect }: IntegrationsProps) => {
  const { t } = useTranslation();
  const history = useHistory();

  const company = useCompanyConfiguration();

  const [currentCategory, setCurrentCategory] = useState(IntegrationCategory.ALL);

  const queryIntegration = getQueryParams<string>(IntegrationQueryParams.NAME);
  const queryCategory = getQueryParams<IntegrationCategory>(IntegrationQueryParams.CATEGORY);

  const integrationCards = useGetIntegrationCards();

  useEffect(() => {
    if (queryIntegration && onIntegrationSelect) {
      const integration = queryIntegration.toUpperCase() as Integration;

      if (isEnabledIntegration(Integration[integration as any as keyof typeof Integration]))
        onIntegrationSelect(Integration[integration as any as keyof typeof Integration]);
    }
  }, [queryIntegration, company, onIntegrationSelect, t]);

  useEffect(() => {
    if (
      queryCategory &&
      queryCategory !== currentCategory &&
      Object.keys(IntegrationCategory).includes(queryCategory)
    ) {
      setCurrentCategory(queryCategory);
    }
  }, []);

  useEffect(() => {
    setQueryParams(IntegrationQueryParams.CATEGORY, currentCategory);
  }, [currentCategory]);

  const onClickCard = (integration: IntegrationCard) => {
    if (onIntegrationSelect) onIntegrationSelect(integration.title);
    else history.push(INTEGRATION_ROUTE[integration.title]);
  };

  const setCategory = (newCategory: IntegrationCategory) => {
    setCurrentCategory(newCategory);
  };

  const categories = getIntegrationsCategories(t).filter(e =>
    isOnBoarding ? e.category !== IntegrationCategory.BANKING : true,
  );

  const integrationList = integrationCards
    .filter(e => (isOnBoarding ? e.category !== IntegrationCategory.BANKING : true))
    .filter(e => (company?.isFrenchClient ? e.title !== Integration.CODABOX : true))
    .filter(
      e =>
        currentCategory === IntegrationCategory.ALL ||
        e.category === currentCategory ||
        (currentCategory === IntegrationCategory.MY_INTEGRATIONS && e.isActive),
    );

  return (
    <>
      <PageTitle>{t(i18nKeys.SETTINGS.INTEGRATIONS.TITLE)}</PageTitle>
      <Panel>
        <Tabs
          orientation="vertical"
          value={currentCategory}
          onChange={category => {
            if (isNonNull(category)) setCategory(category as IntegrationCategory);
          }}
          variant="pills"
          radius="xl"
          pb="xl"
        >
          {categories.map(({ text, category }) => (
            <Tabs.Tab key={category} value={category} onClick={() => setCategory(category)}>
              {text}
            </Tabs.Tab>
          ))}
        </Tabs>
        <SimpleGrid cols={2}>
          {integrationList.map(integration => (
            <IntegrationsCard
              key={integration.title}
              integration={integration}
              onClick={onClickCard}
            />
          ))}
        </SimpleGrid>
      </Panel>
    </>
  );
};
